import {
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  Button,
  Drawer,
  toast,
} from '@ui/components';
import {
  ErrorDetailsDtoErrorCodeEnum,
  OnboardingEditVehicleRequest,
  VehicleTableDataDto,
} from '@interfaces/models';
import { useOnboardingImportedVehicles } from '@pages/onboarding/hooks';
import { Cancel, Delete } from '@ui/components/icons';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';

import {
  useEditVehiclesDrawerForm,
  EditVehiclesForm,
} from './edit-vehicles-drawer-form/use-edit-vehicles-drawer-form';
import { EditVehiclesDrawerForm } from './edit-vehicles-drawer-form/edit-vehicles-drawer-form';

export const EditVehicleDrawer = ({
  showRemoveVehicleDialog,
  onClose,
  isOpen,
  data,
}: {
  onClose: ({ hasBeenModified }?: { hasBeenModified?: boolean }) => void;
  showRemoveVehicleDialog: () => void;
  data: VehicleTableDataDto;
  isOpen: boolean;
}) => {
  const { t } = useTranslation();

  const { form } = useEditVehiclesDrawerForm();

  const { editVehicle, isEditVehiclePending } = useOnboardingImportedVehicles({
    onEditVehicleSuccess: () => {
      toast.success(t('ONBOARDING.IMPORT_VEHICLES.VEHICLE_EDITED_SUCCESSFULLY'));
      onClose({
        hasBeenModified: true,
      });
    },
    onEditVehicleError: (error) => {
      if (error === ErrorDetailsDtoErrorCodeEnum.REGISTRATION_NUMBER_ALREADY_EXISTS) {
        form.setError('registrationNumber', {
          message: t('API_ERRORS.REGISTRATION_NUMBER_ALREADY_EXISTS'),
        });
      } else {
        toast.error(t(`API_ERRORS.DEFAULT`));
      }
    },
  });

  const handleEditVehicle = (vehicle: Omit<OnboardingEditVehicleRequest, 'id'>) => {
    editVehicle({ vehicle: { ...vehicle, id: data.id }, vehicleId: data.id! });
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  // set form values
  useEffect(() => {
    const formValues: EditVehiclesForm = {
      registrationCountry: data.registrationCountry,
      registrationNumber: data.registrationNumber,
      vehicleCategory: data.vehicleCategory,
      labels: data.labels?.map((label) => label.id),
      name: data.name,
    };

    form.reset(formValues);
  }, [data, form]);

  return (
    <Drawer onOpenChange={handleClose} direction="right" open={isOpen} handleOnly>
      <DrawerContent>
        <DrawerHeader className="flex">
          <DrawerTitle>{t('ONBOARDING.IMPORT_VEHICLES.EDIT_VEHICLE_DRAWER_TITLE')}</DrawerTitle>
          <div className="flex ml-auto">
            <Button onClick={showRemoveVehicleDialog} variant="ghost" type="button">
              <Delete />
            </Button>
            <Button onClick={handleClose} variant="ghost" type="button">
              <Cancel />
            </Button>
          </div>
        </DrawerHeader>
        <div className="px-4">
          <EditVehiclesDrawerForm form={form} />
        </div>
        <DrawerFooter>
          <Button
            onClick={form.handleSubmit((data) => {
              // TODO: revert this
              handleEditVehicle(data);
            })}
            isLoading={isEditVehiclePending}
            disabled={isEditVehiclePending}
            size="large"
            wide
          >
            {t('ONBOARDING.IMPORT_VEHICLES.EDIT_VEHICLE_DRAWER_SAVE_CHANGES')}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
